import React from 'react';

export default function RsvpControls({
  enabled,
  isFull,
  myRsvpStatus,
  myOccurrenceRsvpStatus = null,
  mySeriesRsvpStatus = null,
  isRecurring,
  occurrenceId,
  setRsvpStatus,
  resetRsvpStatus,
  rsvpBusy,
  capacityNum,
  currentYesCount,
  capacityRemaining = null,
  rsvpSeriesResponsesEnabled = true,
  title = 'Your RSVP',
  readOnly = false,
  readOnlyMessage = '',
}) {
  if (!enabled) return null;

  const remaining = capacityRemaining == null
    ? Math.max(0, capacityNum - currentYesCount)
    : capacityRemaining;
  const spotsText = capacityNum == null
    ? 'No capacity set'
    : remaining <= 0 && myRsvpStatus === 'yes'
      ? 'You have a place · Event full'
      : remaining <= 0
        ? 'Event full'
        : `Spots remaining: ${remaining}`;

  const goingSelected = myRsvpStatus === 'yes';
  const notGoingSelected = myRsvpStatus === 'no';
  const activeOccurrenceId = occurrenceId || null;
  const seriesSelected = mySeriesRsvpStatus === 'yes';
  const seriesNotSelected = mySeriesRsvpStatus === 'no';
  const occurrenceSelected = myOccurrenceRsvpStatus === 'yes';
  const occurrenceNotSelected = myOccurrenceRsvpStatus === 'no';
  const allowSeriesResponses = !isRecurring || rsvpSeriesResponsesEnabled !== false;
  const canResetOccurrence = myOccurrenceRsvpStatus != null;
  const canResetSeries = isRecurring
    ? allowSeriesResponses && (myOccurrenceRsvpStatus != null || mySeriesRsvpStatus != null)
    : myRsvpStatus != null;
  const disabled = rsvpBusy || readOnly;
  const requireOccurrenceDate = () => {
    if (activeOccurrenceId) return true;
    alert('Choose a date first for "this date" RSVP.');
    return false;
  };
  const confirmResetOccurrence = () => {
    if (typeof window === 'undefined') return true;
    return window.confirm('Reset only this occurrence back to Awaiting RSVP?');
  };
  const confirmResetSeries = () => {
    if (typeof window === 'undefined') return true;
    if (isRecurring && activeOccurrenceId) {
      return window.confirm('Reset this date and later dates back to Awaiting RSVP? Earlier saved occurrence responses will be kept.');
    }
    if (isRecurring) {
      return window.confirm('Reset all RSVP responses for this series back to Awaiting RSVP?');
    }
    return window.confirm('Reset this RSVP back to Awaiting RSVP?');
  };
  const handleOccurrenceReset = () => {
    if (!resetRsvpStatus || !canResetOccurrence || disabled || !activeOccurrenceId) return;
    if (!confirmResetOccurrence()) return;
    resetRsvpStatus(
      { mode: 'occurrence_only', occurrenceId: activeOccurrenceId }
    );
  };
  const handleSeriesReset = () => {
    if (!resetRsvpStatus || !canResetSeries || disabled) return;
    if (!confirmResetSeries()) return;
    resetRsvpStatus(
      isRecurring && activeOccurrenceId
        ? { mode: 'from_occurrence', occurrenceId: activeOccurrenceId }
        : {}
    );
  };
  const formatStatusLabel = (value) => {
    if (value === 'yes') return 'Going';
    if (value === 'no') return 'Not going';
    return 'No response';
  };
  const recurringResetLabel = activeOccurrenceId ? 'Reset this date onward' : 'Reset all responses';
  const recurringResetHint = activeOccurrenceId
    ? 'Reset clears this date and any later saved responses back to Awaiting RSVP.'
    : 'Reset clears all saved responses in this series back to Awaiting RSVP.';

  return (
    <div
      className="card"
      style={{
        padding: 16,
        borderRadius: 12,
        border: '1px solid #eee',
      }}
    >
      <div style={{ marginBottom: 8, fontWeight: 600 }}>{title}</div>
      {readOnly ? (
        <div className="text-muted small" style={{ marginBottom: 10 }}>
          {readOnlyMessage || 'RSVPs are now read-only.'}
        </div>
      ) : null}

      {isRecurring ? (
        <>
          <div
            style={{
              marginBottom: 10,
              paddingBottom: 10,
              borderBottom: '1px solid #e5e7eb',
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              This occurrence
            </div>
            <div className="text-muted small">
              Applies only to the event instance you opened from the calendar.
            </div>
            <div className="text-muted small" style={{ marginTop: 4 }}>
              Current: {formatStatusLabel(myOccurrenceRsvpStatus)}
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              flexWrap: 'wrap',
              marginBottom: 8,
            }}
          >
            <button
              type="button"
              onClick={() =>
                requireOccurrenceDate() &&
                setRsvpStatus('yes', {
                  scope: 'occurrence',
                  occurrenceId: activeOccurrenceId,
                })
              }
              disabled={
                disabled ||
                (isFull && myRsvpStatus !== 'yes')
              }
              style={{
                padding: '6px 14px',
                borderRadius: 20,
                fontWeight: 600,
                border:
                  occurrenceSelected
                    ? '2px solid #198754'
                    : '1px solid #ccc',
                background:
                  occurrenceSelected ? '#dff6e9' : '#f8f9fa',
                color:
                  occurrenceSelected ? '#198754' : '#555',
              }}
            >
              Going this date
            </button>
            <button
              type="button"
              onClick={() =>
                requireOccurrenceDate() &&
                setRsvpStatus('no', {
                  scope: 'occurrence',
                  occurrenceId: activeOccurrenceId,
                })
              }
              disabled={disabled}
              style={{
                padding: '6px 14px',
                borderRadius: 20,
                fontWeight: 600,
                border:
                  occurrenceNotSelected
                    ? '2px solid #dc3545'
                    : '1px solid #ccc',
                background:
                  occurrenceNotSelected
                    ? '#fbe6e8'
                    : '#f8f9fa',
                color:
                  occurrenceNotSelected ? '#dc3545' : '#555',
              }}
            >
              Not this date
            </button>
          </div>
          {canResetOccurrence && activeOccurrenceId ? (
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                flexWrap: 'wrap',
                marginBottom: 8,
              }}
            >
              <button
                type="button"
                onClick={handleOccurrenceReset}
                disabled={disabled}
                style={{
                  padding: '6px 14px',
                  borderRadius: 20,
                  fontWeight: 600,
                  border: '1px solid #cbd5e1',
                  background: '#f8fafc',
                  color: '#475569',
                }}
              >
                Reset this date
              </button>
            </div>
          ) : null}

          {allowSeriesResponses ? (
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                flexWrap: 'wrap',
                marginTop: 14,
                paddingTop: 10,
                borderTop: '1px solid #e5e7eb',
              }}
            >
              <div style={{ width: '100%', fontWeight: 600, marginBottom: 2 }}>
                Series response
              </div>
              <div className="text-muted small" style={{ width: '100%', marginBottom: 2 }}>
                Current: {formatStatusLabel(mySeriesRsvpStatus)}
              </div>
              <div className="text-muted small" style={{ width: '100%', marginBottom: 4 }}>
                {recurringResetHint}
              </div>
              <button
                type="button"
                onClick={() => setRsvpStatus('yes', { scope: 'series' })}
                disabled={disabled || (isFull && myRsvpStatus !== 'yes')}
                style={{
                  padding: '6px 14px',
                  borderRadius: 20,
                  fontWeight: 600,
                  border:
                    seriesSelected
                      ? '2px solid #198754'
                      : '1px solid #ccc',
                  background:
                    seriesSelected ? '#dff6e9' : '#f8f9fa',
                  color: seriesSelected ? '#198754' : '#555',
                }}
              >
                Going all dates
              </button>
              <button
                type="button"
                onClick={() => setRsvpStatus('no', { scope: 'series' })}
                disabled={disabled}
                style={{
                  padding: '6px 14px',
                  borderRadius: 20,
                  fontWeight: 600,
                  border:
                    seriesNotSelected
                      ? '2px solid #dc3545'
                      : '1px solid #ccc',
                  background:
                    seriesNotSelected ? '#fbe6e8' : '#f8f9fa',
                  color: seriesNotSelected ? '#dc3545' : '#555',
                }}
              >
                Not all dates
              </button>
            </div>
          ) : null}
          {canResetSeries ? (
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                flexWrap: 'wrap',
                marginTop: 8,
              }}
            >
              <button
                type="button"
                onClick={handleSeriesReset}
                disabled={disabled}
                style={{
                  padding: '6px 14px',
                  borderRadius: 20,
                  fontWeight: 600,
                  border: '1px solid #cbd5e1',
                  background: '#f8fafc',
                  color: '#475569',
                }}
              >
                {recurringResetLabel}
              </button>
            </div>
          ) : null}
        </>
      ) : (
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            onClick={() => setRsvpStatus('yes')}
            disabled={disabled || (isFull && !goingSelected)}
            style={{
              padding: '6px 14px',
              borderRadius: 20,
              fontWeight: 600,
              border: goingSelected ? '2px solid #198754' : '1px solid #ccc',
              background: goingSelected ? '#dff6e9' : '#f8f9fa',
              color: goingSelected ? '#198754' : '#555',
            }}
          >
            Going
          </button>

          <button
            type="button"
            onClick={() => setRsvpStatus('no')}
            disabled={disabled}
            style={{
              padding: '6px 14px',
              borderRadius: 20,
              fontWeight: 600,
              border: notGoingSelected ? '2px solid #dc3545' : '1px solid #ccc',
              background: notGoingSelected ? '#fbe6e8' : '#f8f9fa',
              color: notGoingSelected ? '#dc3545' : '#555',
            }}
          >
            Not attending
          </button>
          {canResetSeries ? (
            <div
              style={{
                width: '100%',
                display: 'flex',
                gap: 8,
                marginTop: 4,
              }}
            >
              <button
                type="button"
                onClick={handleSeriesReset}
                disabled={disabled}
                style={{
                  padding: '6px 14px',
                  borderRadius: 20,
                  fontWeight: 600,
                  border: '1px solid #cbd5e1',
                  background: '#f8fafc',
                  color: '#475569',
                }}
              >
                Reset RSVP
              </button>
            </div>
          ) : null}
        </div>
      )}

      <div style={{ marginTop: 8 }}>
        <span className="text-muted small">{spotsText}</span>
      </div>
    </div>
  );
}
